import EdgeCurveProgram from '@sigma/edge-curve';
import Graph from 'graphology';
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import Sigma from 'sigma';
import type { NodeDisplayData } from 'sigma/types';

import type { SigmaEdgeAttributes, SigmaNodeAttributes } from '../lib/graph-node-attributes.js';
import { drawSigmaNodeHover, drawSigmaNodeLabel } from '../lib/sigma-node-canvas-renderers.js';

import { createLayoutController } from './sigma-layout-controller.js';

export type SigmaNodeReducer = (node: string, data: NodeDisplayData) => Partial<NodeDisplayData>;

export interface UseSigmaOptions {
  readonly onNodeClick?: (nodeId: string) => void;
  readonly onStageClick?: () => void;
}

export interface UseSigmaReturn {
  readonly containerRef: MutableRefObject<HTMLDivElement | null>;
  readonly sigma: Sigma | null;
  readonly setGraph: (graph: Graph<SigmaNodeAttributes, SigmaEdgeAttributes>) => void;
  readonly zoomTo: (nodeId: string) => void;
  readonly resetZoom: () => void;
  readonly isLayoutRunning: boolean;
  readonly stopLayout: () => void;
  readonly restartLayout: () => void;
  readonly getNodeLabel: (nodeId: string) => string | null;
}

export function useSigma(opts: UseSigmaOptions = {}): UseSigmaReturn {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sigmaRef = useRef<Sigma | null>(null);
  const graphRef = useRef<Graph<SigmaNodeAttributes, SigmaEdgeAttributes> | null>(null);
  const [isLayoutRunning, setIsLayoutRunning] = useState(false);
  const [sigmaInstance, setSigmaInstance] = useState<Sigma | null>(null);

  const optsRef = useRef(opts);
  useEffect(() => {
    optsRef.current = opts;
  }, [opts]);

  const layout = useMemo(() => createLayoutController(sigmaRef, setIsLayoutRunning), []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const sigma = new Sigma(
      new Graph<SigmaNodeAttributes, SigmaEdgeAttributes>(),
      container,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Sigma settings type narrowed manually
      {
        renderLabels: true,
        labelSize: 12,
        labelWeight: '600',
        labelColor: { color: '#ffffff' },
        labelRenderedSizeThreshold: 10,
        labelDensity: 0.1,
        defaultNodeColor: '#6b7280',
        defaultEdgeColor: '#2a2a3a',
        defaultEdgeType: 'curved',
        edgeProgramClasses: { curved: EdgeCurveProgram },
        minCameraRatio: 0.002,
        maxCameraRatio: 50,
        hideEdgesOnMove: true,
        hideLabelsOnMove: true,
        zIndex: true,
        drawLabel: drawSigmaNodeLabel,
        defaultDrawNodeHover: drawSigmaNodeHover,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any,
    ) as unknown as Sigma;
    sigmaRef.current = sigma;
    setSigmaInstance(sigma);

    sigma.on('clickNode', ({ node }) => optsRef.current.onNodeClick?.(node));
    sigma.on('clickStage', () => optsRef.current.onStageClick?.());
    sigma.on('enterNode', () => {
      if (containerRef.current) containerRef.current.style.cursor = 'pointer';
    });
    sigma.on('leaveNode', () => {
      if (containerRef.current) containerRef.current.style.cursor = 'grab';
    });

    return (): void => {
      layout.dispose();
      sigma.kill();
      sigmaRef.current = null;
      setSigmaInstance(null);
      graphRef.current = null;
    };
  }, [layout]);

  const setGraph = useCallback(
    (newGraph: Graph<SigmaNodeAttributes, SigmaEdgeAttributes>) => {
      const sigma = sigmaRef.current;
      if (!sigma) return;
      layout.dispose();
      graphRef.current = newGraph;
      sigma.setGraph(newGraph);
      layout.start(newGraph);
      void sigma.getCamera().animatedReset({ duration: 500 });
    },
    [layout],
  );

  const zoomTo = useCallback((nodeId: string) => {
    const sigma = sigmaRef.current;
    const graph = graphRef.current;
    if (!sigma || !graph?.hasNode(nodeId)) return;
    const attr = graph.getNodeAttributes(nodeId);
    void sigma.getCamera().animate({ x: attr.x, y: attr.y, ratio: 0.15 }, { duration: 400 });
  }, []);

  const resetZoom = useCallback(() => {
    void sigmaRef.current?.getCamera().animatedReset({ duration: 300 });
  }, []);

  const stopLayout = useCallback(() => {
    layout.stop(graphRef.current);
  }, [layout]);

  const restartLayout = useCallback(() => {
    const graph = graphRef.current;
    if (!graph) return;
    layout.start(graph);
  }, [layout]);

  const getNodeLabel = useCallback((nodeId: string): string | null => {
    const graph = graphRef.current;
    if (!graph?.hasNode(nodeId)) return null;
    return graph.getNodeAttribute(nodeId, 'label') ?? null;
  }, []);

  return {
    containerRef,
    sigma: sigmaInstance,
    setGraph,
    zoomTo,
    resetZoom,
    isLayoutRunning,
    stopLayout,
    restartLayout,
    getNodeLabel,
  };
}
